// AI-CONTEXT: See docs/features/msi-effect-monetisation.md
import { useEffect, useState } from 'react'
import { db } from '../firebase/config'
import { client } from './Client'
import notDeleted from '../helpers/notDeleted'
import useMsiMonetaryScores from './useMsiMonetaryScores'
import useInterventionDoseContext from './useInterventionDoseContext'
import { fetchEffectData } from './useMsiMonetaryTotal'
import computeMsiEffectValue from '../helpers/msiMonetisationHelpers/computeMsiEffectValue'
import { hasScoresFor, scoresForEffect } from '../helpers/meetstandaard/buildScoresFromStandaardDocument'
import msiValueExclusion from '../helpers/msiMonetisationHelpers/msiValueExclusion'
import defaultDates from '../utils/defaultDates'

/**
 * MSI monetisation for ONE effect, for the €-line on the effect card. Same
 * data path and rules as the tenant-wide useMsiMonetaryTotal: records via
 * fetchHierarchyData (session-cached), Participatieladder hard-excluded,
 * effects without meetstandaard amounts flagged noScores — both without any
 * fetching. Fail visibly: a scores or records error surfaces as `error`,
 * never as a partial value.
 *
 * @param {{ ID?: string, MSIId?: string, Effect?: string, Standard?: string } | null} effect - the OutputEffects doc
 * @returns {{ value: object | null, scores: Array<object> | null, msiId: string | null, effectName: string, loading: boolean, error: Error | null }}
 */
const useMsiEffectValue = (effect) => {
    const { scoresByMsiId, loading: scoresLoading, error: scoresError } = useMsiMonetaryScores()
    const [state, setState] = useState({ value: null, scores: null, loading: true, error: null })
    const isMsi = effect?.Standard === 'true' && !!effect?.MSIId && !!effect?.ID
    const msiId = isMsi ? effect.MSIId : ''
    const effectId = effect?.ID || ''
    // De participatieladder, of een effect waarvan een ander effect de waarde
    // draagt. Een string, zodat hij als dependency niet bij elke snapshot wisselt.
    const excludedReason = msiValueExclusion(effect)?.reason || ''

    // Rekenregels MSI: duur en uren per deelnemer + standaard van de organisatie.
    // Alleen voor een MSI-effect dat meetelt: een publieke kaart ('' als id) mag
    // geen Firestore lezen (ADR-022), en andere effecten hebben het niet nodig.
    const dose = useInterventionDoseContext({ enabled: isMsi && !excludedReason })

    useEffect(() => {
        if (!isMsi) {
            setState({ value: null, scores: null, loading: false, error: null })
            return
        }
        if (excludedReason) {
            setState({ value: { excluded: true, excludedReason }, scores: null, loading: false, error: null })
            return
        }
        if (scoresLoading || dose.loading) return
        if (dose.error) {
            setState({ value: null, scores: null, loading: false, error: dose.error })
            return
        }
        if (scoresError || !scoresByMsiId) {
            setState({ value: null, scores: null, loading: false, error: scoresError || new Error('MSI-bedragen niet geladen') })
            return
        }

        if (!hasScoresFor(scoresByMsiId, msiId)) {
            setState({ value: { excluded: false, noScores: true }, scores: null, loading: false, error: null })
            return
        }

        let cancelled = false
        setState({ value: null, scores: null, loading: true, error: null })
        const { defaultStartDate, defaultEndDate } = defaultDates()

        fetchEffectData(effect, defaultStartDate, defaultEndDate)
            .then((data) => {
                if (cancelled) return
                // Which version's amounts apply is only known from the rows:
                // an effect adopted under 0.9 is valued at 0.9 (methodiek §5.3).
                const { scores, standaardVersion, versionProblem } = scoresForEffect(scoresByMsiId, msiId, data.rowDefs)
                if (!scores) {
                    setState({ value: { excluded: false, noScores: true, standaardVersion, versionProblem }, scores: null, loading: false, error: null })
                    return
                }
                setState({
                    value: {
                        excluded: false,
                        standaardVersion,
                        ...computeMsiEffectValue({ ...data, scores, personasById: dose.context.personasById, orgDefault: dose.context.orgDefault }),
                    },
                    scores,
                    loading: false,
                    error: null,
                })
            })
            .catch((err) => {
                console.error('useMsiEffectValue error:', err)
                if (cancelled) return
                setState({ value: null, scores: null, loading: false, error: err })
            })

        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectId, msiId, isMsi, excludedReason, scoresByMsiId, scoresLoading, scoresError, dose.context, dose.loading, dose.error])

    // The effect's monetary scores (in its own version) travel along in state
    // for consumers that project values (goal prognose) — null for
    // non-MSI/excluded/no-amounts effects. msiId + effectName travel along for
    // the onderbouwing join (ScoreBlock holds only the OutputEffects.ID, not the doc).
    return { ...state, msiId: isMsi ? msiId : null, effectName: effect?.Effect || '' }
}

// One-shot doc resolution shared across mounts. Every ScoreBlock on a page
// calls useMsiEffectValueById — a real-time listener per chart just to read
// Standard/MSIId off a doc would be the listener-per-list-item anti-pattern,
// so resolve once per effect id per session with a plain .get().
const effectDocCache = new Map()

export const clearEffectDocCacheForTests = () => effectDocCache.clear()

const resolveEffectDoc = (effectId) => {
    if (!effectDocCache.has(effectId)) {
        const promise = db.collection('OutputEffects')
            .where('CompagnyID', '==', client)
            .where('ID', '==', effectId)
            .limit(1)
            .get()
            .then((snap) => snap.docs.map((d) => d.data()).filter(notDeleted)[0] || null)
            .catch((err) => {
                // Don't cache failures — the next mount may retry.
                effectDocCache.delete(effectId)
                throw err
            })
        effectDocCache.set(effectId, promise)
    }
    return effectDocCache.get(effectId)
}

/**
 * Variant for consumers that only hold the effect id (ScoreBlock receives
 * `effectId`, not the doc): resolves the OutputEffects doc first with a
 * cached one-shot read. Pass '' to skip entirely (returns a null value
 * without fetching — the public-card path).
 *
 * @param {string} effectId - OutputEffects.ID, or '' to skip
 * @returns {{ value: object | null, scores: Array<object> | null, msiId: string | null, effectName: string, loading: boolean, error: Error | null }}
 */
export const useMsiEffectValueById = (effectId) => {
    const [effect, setEffect] = useState(/** @type {object | null} */ (null))

    useEffect(() => {
        if (!effectId) {
            setEffect(null)
            return
        }
        let cancelled = false
        resolveEffectDoc(effectId)
            .then((doc) => { if (!cancelled) setEffect(doc) })
            .catch((err) => {
                console.error('useMsiEffectValueById error:', err)
                if (!cancelled) setEffect(null)
            })
        return () => { cancelled = true }
    }, [effectId])

    return useMsiEffectValue(effect)
}

export default useMsiEffectValue
